import { z } from "zod";

// BaseProduct schema
export const BaseProductSchema = z.object({
  name: z.string(),
  id: z.string(),
  price: z.number(),
  partial_amount: z.number().optional(),
  image: z.string(),
  currency: z.literal("USD"),
  display_price: z.string(),
});

// FirstHandProduct schema
export const FirstHandProductSchema = BaseProductSchema.extend({
  product_data: z.object({
    metadata: z.object({
      type: z.literal("first_hand"),
      tee_time_id: z.string(),
      number_of_bookings: z.number(),
      buddies_data: z.array(z.object({
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
        phoneNo: z.string(),
        slotIndex: z.number().optional(),
      })),
    }),
  }),
  price_classes: z.array(z.any()),
  partial_config: z.object({
    enabled: z.boolean(),
    type: z.enum(['FIXED_PER_PLAYER', 'FIXED_TOTAL', 'PERCENT_PER_PLAYER', 'PERCENT_TOTAL']).nullable(),
    value: z.number().nullable(),
  }).nullable().optional(),
});

// SecondHandProduct schema
export const SecondHandProductSchema = BaseProductSchema.extend({
  product_data: z.object({
    metadata: z.object({
      type: z.literal("second_hand"),
      second_hand_id: z.string(),
      buddies_data: z.array(z.object({
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
        phoneNo: z.string(),
        slotIndex: z.number().optional(),
      })),
    }),
  }),
});

// ConvenienceFee schema
export const ConvenienceFeeSchema = BaseProductSchema.extend({
  product_data: z.object({
    metadata: z.object({
      type: z.literal("convenience_fee"),
    }),
  }),
});

// Markup schema
export const MarkupSchema = BaseProductSchema.extend({
  product_data: z.object({
    metadata: z.object({
      type: z.literal("markup"),
    }),
  }),
});

// Tax schema
export const TaxSchema = BaseProductSchema.extend({
  product_data: z.object({
    metadata: z.object({
      type: z.literal("taxes"),
    }),
  }),
});

// SensibleProduct schema
const SensibleProductSchema = BaseProductSchema.extend({
  product_data: z.object({
    metadata: z.object({
      type: z.literal("sensible"),
      sensible_quote_id: z.string(),
    }),
  }),
});

// AuctionProduct schema
export const AuctionProductSchema = BaseProductSchema.extend({
  product_data: z.object({
    metadata: z.object({
      type: z.literal("auction"),
      auction_id: z.string(),
    }),
  }),
});

// CharityProduct schema
export const CharityProductSchema = BaseProductSchema.extend({
  product_data: z.object({
    metadata: z.object({
      type: z.literal("charity"),
      charity_id: z.string(),
      donation_amount: z.number(),
    }),
  }),
});
export const CartFeeSchema = BaseProductSchema.extend({
  product_data: z.object({
    metadata: z.object({
      type: z.literal("cart_fee"),
      amount: z.number(),
    }),
  }),
});
export const CartFeeTaxPercentSchema = BaseProductSchema.extend({
  product_data: z.object({
    metadata: z.object({
      type: z.literal("cartFeeTaxPercent"),
      amount: z.number().optional(),
    }),
  }),
});
export const WeatherGuaranteeTaxPercentSchema = BaseProductSchema.extend({
  product_data: z.object({
    metadata: z.object({
      type: z.literal("weatherGuaranteeTaxPercent"),
    }),
  }),
});
export const MarkupTaxPercentSchema = BaseProductSchema.extend({
  product_data: z.object({
    metadata: z.object({
      type: z.literal("markupTaxPercent"),
    }),
  }),
});
export const GreenFeeTaxPercentSchema = BaseProductSchema.extend({
  product_data: z.object({
    metadata: z.object({
      type: z.literal("greenFeeTaxPercent"),
    }),
  }),
});
export const MerchandiseTaxPercentSchema = BaseProductSchema.extend({
  product_data: z.object({
    metadata: z.object({
      type: z.literal("merchandiseTaxPercent"),
    }),
  }),
})

// Firsthand group schema
export const FirstHandGroupProductSchema = BaseProductSchema.extend({
  product_data: z.object({
    metadata: z.object({
      type: z.literal("first_hand_group"),
      tee_time_ids: z.array(z.string()),
      number_of_bookings: z.number(),
      min_players_per_booking: z.number(),
    }),
  }),
});

// Merchandise Product schema
export const MerchandiseProductSchema = BaseProductSchema.extend({
  product_data: z.object({
    metadata: z.object({
      type: z.literal("merchandise"),
      merchandiseItems: z.array(z.object({
        id: z.string(),
        qty: z.number(),
        pricePerItem: z.number(),
        taxAmountPerItem: z.number(),
      })),
    }),
  }),
});

// Merchandise Product schema with Tax Overrides
export const MerchandiseWithTaxOverrideProductSchema = BaseProductSchema.extend({
  product_data: z.object({
    metadata: z.object({
      type: z.literal("merchandiseWithTaxOverride"),
      priceWithoutTax: z.number(),
      taxAmount: z.number(),
      merchandiseItems: z.array(z.object({
        id: z.string(),
        qty: z.number(),
        merchandiseTaxPercent: z.number(),
        pricePerItem: z.number(),
        taxAmountPerItem: z.number(),
      })),
    }),
  }),
});

// Advanced booking fees schema
export const AdvancedBookingFeeSchema = BaseProductSchema.extend({
  product_data: z.object({
    metadata: z.object({
      type: z.literal("advanced_booking_fees_per_player"),
    }),
  }),
})


// ProductData schema
export const ProductDataSchema = z.union([
  FirstHandProductSchema,
  SecondHandProductSchema,
  ConvenienceFeeSchema,
  MarkupSchema,
  SensibleProductSchema,
  AuctionProductSchema,
  CharityProductSchema,
  TaxSchema,
  CartFeeSchema,
  CartFeeTaxPercentSchema,
  GreenFeeTaxPercentSchema,
  WeatherGuaranteeTaxPercentSchema,
  MarkupTaxPercentSchema,
  FirstHandGroupProductSchema,
  MerchandiseProductSchema,
  MerchandiseTaxPercentSchema,
  MerchandiseWithTaxOverrideProductSchema,
  AdvancedBookingFeeSchema
]);

// CustomerCart schema
export const CustomerCartSchema = z.object({
  userId: z.string(),
  customerId: z.string(),
  courseId: z.string(),
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  promoCode: z.string().nullable().optional(),
  phone_country_code: z.string().nullable(),
  paymentId: z.string().nullable(),
  cart: z.array(ProductDataSchema),
  cartId: z.string().optional(),
  teeTimeId: z.string().optional(),
  courseName: z.string().optional(),
  playDateTime: z.string().optional(),
  playerCount: z.string().optional(),
  teeTimeType: z.string().optional(),
  listingId: z.string().optional(),
  purpose: z.string().optional(),
  isPreAuth: z.boolean(),
});

export const OfferCartSchema = z.object({
  name: z.string(),
  id: z.string().min(1),
  price: z.number().int().min(1),
  initial_price: z.number().int().min(1),
  image: z.string(),
  currency: z.literal("USD"),
  display_price: z.string(),
  metadata: z.object({
    type: z.literal("offer"),
    status: z.string(),
    bookingId: z.string().min(1),
    from: z.string().min(1),
    to: z.string().min(1),
  }),
  buddies_data: z.array(z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    phoneNo: z.string(),
    slotIndex: z.number().optional(),
  })),
});

export const EventCartSchema = z.object({
  name: z.string(),
  id: z.string().min(1),
  price: z.number().int().min(1),
  image: z.string(),
  currency: z.literal("USD"),
  display_price: z.string(),
  collected_tax: z.number().int().min(0),
  buddies_data: z.array(z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    phoneNo: z.string(),
    slotIndex: z.number().optional(),
  })),
  payment_mode: z.string(),
  partial_config: z.object({
    enabled: z.boolean(),
    type: z.enum(['FIXED_PER_PLAYER', 'FIXED_TOTAL', 'PERCENT_PER_PLAYER', 'PERCENT_TOTAL']).nullable(),
    value: z.number().nullable(),
  }).nullable().optional(),
  metadata: z.object({
    type: z.literal("event"),
    eventId: z.string(),
  }),
});

export type OfferCartSchema = z.infer<typeof OfferCartSchema>;
export type EventCartSchemaType = z.infer<typeof EventCartSchema>;